import * as React from "react";
import { ChevronIcon } from "vega-icons";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@vega-core/components/shadcn/dropdown-menu";

import { SplitButtonAction } from "./SplitButtonAction";
import { SplitButtonRoot } from "./SplitButtonRoot";
import { SplitButtonTrigger } from "./SplitButtonTrigger";
import { type SplitButtonMenuItem, type SplitButtonVariant } from "./types";

export type SplitButtonProps = {
  variant?: SplitButtonVariant;
  action: {
    icon?: React.ReactNode;
    label: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    dataTestId?: string;
  };
  menuItems: SplitButtonMenuItem[];
  menuAlign?: "center" | "end" | "start";
  disabled?: boolean;
  className?: string;
};

export const SplitButton = ({
  variant = "primary",
  action,
  menuItems,
  menuAlign = "end",
  disabled = false,
  className,
}: SplitButtonProps) => {
  const actionDisabled = disabled || action.disabled;
  const menuDisabled = disabled;

  return (
    <DropdownMenu>
      <SplitButtonRoot className={className} variant={variant}>
        <SplitButtonAction
          data-testid={action.dataTestId}
          disabled={actionDisabled}
          onClick={action.onClick}
        >
          {action.icon}
          {action.label}
        </SplitButtonAction>
        <DropdownMenuTrigger asChild disabled={menuDisabled}>
          <SplitButtonTrigger disabled={menuDisabled}>
            <ChevronIcon />
          </SplitButtonTrigger>
        </DropdownMenuTrigger>
      </SplitButtonRoot>
      <DropdownMenuContent
        align={menuAlign}
        // don't take focus when closing if there's a dialog open
        onCloseAutoFocus={(e) => {
          if (document.querySelector('[role="dialog"]')) e.preventDefault();
        }}
      >
        {menuItems.map((item, index) => {
          if (item.type === "separator") {
            const prevItem = index > 0 ? menuItems[index - 1] : undefined;
            const nextItem =
              index < menuItems.length - 1 ? menuItems[index + 1] : undefined;
            const prevLabel =
              prevItem && prevItem.type !== "separator"
                ? String(prevItem.label)
                : "";
            const nextLabel =
              nextItem && nextItem.type !== "separator"
                ? String(nextItem.label)
                : "";

            return (
              <DropdownMenuSeparator key={`sep-${prevLabel}-${nextLabel}`} />
            );
          }

          const key =
            typeof item.label === "string" ? item.label : `menu-item-${index}`;

          return (
            <DropdownMenuItem
              className="data-[highlighted]:ring-2 data-[highlighted]:ring-inset data-[highlighted]:ring-brand-default"
              disabled={item.disabled}
              key={key}
              onSelect={item.onSelect}
            >
              {item.icon}
              {item.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
